// Status effects (PLR-8; M12.2). PURE: the active-effect list carried by a living
// entity, the per-tick behaviours (regeneration heals, poison hurts with a 1-HP
// floor, wither has no floor, …), and the passive multipliers other systems read
// (speed → movement, strength/weakness → melee, jump boost → jump). Data in
// data/effects/ names each effect + its tint; the behaviour is code (BLK-4).

export interface EffectDef {
  id: string;
  category: 'beneficial' | 'harmful' | 'neutral';
  /** Particle/screen tint (client). */
  color: string;
  /** Applied once on add (instant health/damage) rather than ticking. */
  instant: boolean;
}

export interface ActiveEffect {
  id: string;
  /** 0-based amplifier (level = amplifier + 1). */
  amplifier: number;
  /** Remaining duration in game ticks (instant effects use 0). */
  ticks: number;
}

/** The minimal host an effect acts on (the Player satisfies it). */
export interface EffectHost {
  health: number;
  applyDamage(amount: number): void;
  heal(amount: number): void;
  addExhaustion(amount: number): void;
}

/** Add/refresh an effect: a stronger or longer application wins (Minecraft rule). */
export function addEffect(set: ActiveEffect[], id: string, amplifier: number, ticks: number): void {
  const existing = set.find((e) => e.id === id);
  if (!existing) { set.push({ id, amplifier, ticks }); return; }
  if (amplifier > existing.amplifier || (amplifier === existing.amplifier && ticks > existing.ticks)) {
    existing.amplifier = amplifier;
    existing.ticks = ticks;
  }
}

export function hasEffect(set: readonly ActiveEffect[], id: string): boolean {
  return set.some((e) => e.id === id);
}

/** Remove an effect (raid consumes Bad Omen on trigger, M14.2). No-op if absent. */
export function removeEffect(set: ActiveEffect[], id: string): void {
  const i = set.findIndex((e) => e.id === id);
  if (i >= 0) set.splice(i, 1);
}

/** Amplifier of an effect (0-based), or -1 if absent. */
export function amplifierOf(set: readonly ActiveEffect[], id: string): number {
  return set.find((e) => e.id === id)?.amplifier ?? -1;
}

/** Apply an instant effect's one-shot result (healing/harming potions). */
export function applyInstant(host: EffectHost, id: string, amplifier: number): void {
  const magnitude = 4 << amplifier; // 4, 8, 16, …
  if (id === 'instant_health') host.heal(magnitude / 2);
  else if (id === 'instant_damage') host.applyDamage(magnitude / 2);
}

/**
 * Advance every duration effect one tick, applying its behaviour and removing
 * expired ones. `worldTick` gives the cadence phase so periodic effects fire on
 * a stable schedule. Returns true if the set changed (client HUD refresh).
 */
export function tickEffects(set: ActiveEffect[], host: EffectHost, worldTick: number): boolean {
  let changed = false;
  for (let i = set.length - 1; i >= 0; i--) {
    const e = set[i]!;
    const amp = e.amplifier;
    switch (e.id) {
      case 'regeneration': {
        const interval = Math.max(2, 50 >> amp);
        if (worldTick % interval === 0) host.heal(1);
        break;
      }
      case 'poison': {
        const interval = Math.max(5, 25 >> amp);
        if (worldTick % interval === 0 && host.health > 1) host.applyDamage(1); // poison floor
        break;
      }
      case 'wither': {
        const interval = Math.max(5, 40 >> amp);
        if (worldTick % interval === 0) host.applyDamage(1); // no floor
        break;
      }
      case 'hunger':
        host.addExhaustion(0.005 * (amp + 1));
        break;
      default:
        break;
    }
    if (--e.ticks <= 0) { set.splice(i, 1); changed = true; }
  }
  return changed;
}

/** Potion → the effect it grants (id, amplifier, duration ticks). Instant potions
 *  use 0 ticks. The `potion` item's `potion` component names one of these. */
export const POTIONS: Record<string, { effect: string; amplifier: number; ticks: number }> = {
  swiftness: { effect: 'speed', amplifier: 0, ticks: 3600 },
  swiftness_2: { effect: 'speed', amplifier: 1, ticks: 1800 },
  strength: { effect: 'strength', amplifier: 0, ticks: 3600 },
  leaping: { effect: 'jump_boost', amplifier: 0, ticks: 3600 },
  regeneration: { effect: 'regeneration', amplifier: 0, ticks: 900 },
  poison: { effect: 'poison', amplifier: 0, ticks: 900 },
  healing: { effect: 'instant_health', amplifier: 0, ticks: 0 },
  harming: { effect: 'instant_damage', amplifier: 0, ticks: 0 },
  fire_resistance: { effect: 'fire_resistance', amplifier: 0, ticks: 3600 },
  water_breathing: { effect: 'water_breathing', amplifier: 0, ticks: 3600 },
};

/** Whether an effect id is applied instantly (vs over a duration). */
export function isInstantEffect(id: string): boolean {
  return id === 'instant_health' || id === 'instant_damage';
}

/** The beneficial effects (a splash of these reaches even a creative/invulnerable
 *  player, since they're a gift, not an attack — MB.4). */
const BENEFICIAL_EFFECTS = new Set([
  'instant_health', 'regeneration', 'speed', 'strength', 'jump_boost', 'resistance',
  'absorption', 'fire_resistance', 'water_breathing', 'night_vision', 'slow_falling', 'haste',
  'conduit_power', 'dolphins_grace',
]);
export function isBeneficialEffect(id: string): boolean {
  return BENEFICIAL_EFFECTS.has(id);
}

/** Walk-speed multiplier from speed/slowness (±20% / −15% per level). */
export function speedMultiplier(set: readonly ActiveEffect[]): number {
  const speed = amplifierOf(set, 'speed');
  const slow = amplifierOf(set, 'slowness');
  let m = 1;
  if (speed >= 0) m *= 1 + 0.2 * (speed + 1);
  if (slow >= 0) m *= Math.max(0, 1 - 0.15 * (slow + 1));
  return m;
}

/** Extra jump velocity from Jump Boost (Minecraft +0.1/level, scaled to JUMP_V). */
export function jumpBoost(set: readonly ActiveEffect[]): number {
  const amp = amplifierOf(set, 'jump_boost');
  return amp >= 0 ? 0.1 * (amp + 1) : 0;
}

/** Block-breaking speed multiplier from Haste (+20%/level) and Mining Fatigue
 *  (×0.3 per level — MC; the elder guardian's aura is level III). Multiplicative;
 *  1 when neither is present. Fed into breaktime's `digSpeedMultiplier` opt. */
export function digSpeedMultiplier(set: readonly ActiveEffect[]): number {
  let m = 1;
  const haste = amplifierOf(set, 'haste');
  if (haste >= 0) m *= 1 + 0.2 * (haste + 1);
  const fatigue = amplifierOf(set, 'mining_fatigue');
  if (fatigue >= 0) m *= Math.pow(0.3, fatigue + 1);
  return m;
}

/** Additive melee damage from strength, subtractive from weakness (PLR-3). */
export function meleeDamageDelta(set: readonly ActiveEffect[]): number {
  const str = amplifierOf(set, 'strength');
  const weak = amplifierOf(set, 'weakness');
  let d = 0;
  if (str >= 0) d += 3 * (str + 1);
  if (weak >= 0) d -= 4 * (weak + 1);
  return d;
}

/** Incoming-damage multiplier from Resistance (−20% per level, floored at 0 = full
 *  immunity at Resistance V). Applied after armour, before the health/absorption pool. */
export function resistanceMultiplier(set: readonly ActiveEffect[]): number {
  const amp = amplifierOf(set, 'resistance');
  return amp >= 0 ? Math.max(0, 1 - 0.2 * (amp + 1)) : 1;
}

/** Absorption pool granted by the Absorption effect (4 HP = 2 hearts per level). */
export function absorptionAmount(set: readonly ActiveEffect[]): number {
  const amp = amplifierOf(set, 'absorption');
  return amp >= 0 ? 4 * (amp + 1) : 0;
}

/** Slow Falling (and Levitation) cancel fall damage entirely (MC). */
export function negatesFallDamage(set: readonly ActiveEffect[]): boolean {
  return hasEffect(set, 'slow_falling') || hasEffect(set, 'levitation');
}

/** Per-tick vertical velocity nudge from Levitation (up) — MC eases toward
 *  0.05·(level+1) blocks/tick. Returns 0 when not levitating. */
export function levitationDelta(set: readonly ActiveEffect[], vy: number): number {
  const amp = amplifierOf(set, 'levitation');
  if (amp < 0) return 0;
  const target = 0.05 * (amp + 1);
  return (target - vy) * 0.2; // eased approach, applied additively to vy
}

/** Slow Falling clamps downward speed (MC ≈ −0.7·? → we cap the fall at this). */
export function slowFallingActive(set: readonly ActiveEffect[]): boolean {
  return hasEffect(set, 'slow_falling');
}
